export interface ActionOptions {
  noDelay?: boolean;
  noPause?: boolean;
  unDoable?: boolean;
}

export interface CustomAction<UIPayloadType> {
  id: string;
  uiAction: string;
  noDelay: boolean;
  noPause: boolean;
  unDoable: boolean;
  aCreator: (payload: UIPayloadType) => {};
}

export type ActionCreatorType = <UIPayloadType>(
  identifier: string,
  options?: ActionOptions
) => CustomAction<UIPayloadType>;

export const actionCreator: ActionCreatorType = (
  actionIdentifier: string,
  options: ActionOptions = {}
) => {
  return {
    id: actionIdentifier,
    uiAction: `UI_${actionIdentifier}`,
    noDelay: !!options.noDelay,
    noPause: !!options.noPause,
    unDoable: !!options.unDoable,
    aCreator: (payload) => ({
      type: `UI_${actionIdentifier}`,
      payload,
    }),
  };
};

const ac = actionCreator;

const NoPauseNoDelay = {
  noPause: true,
  noDelay: true,
};
interface CustomWindowDataItemPayload {
  window: string;
  [key: string]: any;
}

// intentional ignoration of prettier for better readability, please mind your formatting
/* prettier-ignore */
export const ComposedActions = {
  SystemPlaceTrackSymbolOnTacsitImage: ac<void>('SystemPlaceTrackSymbolOnTacsitImage', { noPause: true }),
  SystemMoveSymbolOnTacsitImage: ac<void>(`SystemMoveSymbolOnTacsitImage`, NoPauseNoDelay),
  SystemSetGeneralButtonLED: ac<void>('SystemSetGeneralButtonLED', { noPause: true }),
  PressGeneralButtonSwitch: ac<{ id: string; isActive: boolean }>('PressGeneralButtonSwitch', {unDoable: true}),
  SystemUpdateAreaCircleProperties: ac<void>('SystemUpdateAreaCircleProperties', NoPauseNoDelay),
  SystemUpdateAreaPolygonProperties: ac<void>('SystemUpdateAreaPolygonProperties', NoPauseNoDelay),
  SystemUpdatePolylineProperties: ac<void>('SystemUpdatePolylineProperties', NoPauseNoDelay),
  SystemHideTacsitRangeInOut: ac<void>('SystemHideTacsitRangeInOut', NoPauseNoDelay),
  SystemShowTacsitRangeInOut: ac<void>('SystemShowTacsitRangeInOut', NoPauseNoDelay),
  SystemStopWindowCountdown: ac<void>('SystemStopWindowCountdown', NoPauseNoDelay),
  PushWindowToggleButton: ac<{ windowId: string, key: string, value: boolean }>('PushWindowToggleButton', {unDoable: true}),
  ClickButtonOnReviewAlertsPanel: ac<{ id: string }>('ClickButtonOnReviewAlertsPanel'),
  SystemPrimaryHookPoint: ac<void>('SystemPrimaryHookPoint'),
  SystemSecondaryHookPoint: ac<void>('SystemSecondaryHookPoint'),
  SelectWindowDataItems: ac<{actionPayload: CustomWindowDataItemPayload}>('SelectWindowDataItems', {unDoable: true}),
  UnselectWindowDataItems: ac<{actionPayload: CustomWindowDataItemPayload}>('UnselectWindowDataItems', {unDoable: true}),
  SystemShowCatizTab: ac<void>('SystemShowCatizTab', NoPauseNoDelay),
  SystemChangeTouchPanelSet: ac<void>("SystemChangeTouchPanelSet", NoPauseNoDelay),
  PushWindowButton: ac<{ windowId: string, buttonId: string }>("PushWindowButton"),
  SetWindowActiveTab: ac<{ windowId: string, tabId: string }>("SetWindowActiveTab"),
  SystemPlaceAreaChannel: ac<void>("SystemPlaceAreaChannel", NoPauseNoDelay),
  ShowTextInTsdTutorialArea: ac<void>("ShowTextInTsdTutorialArea", NoPauseNoDelay),
  SystemUpdateTrackRectangle: ac<void>("SystemUpdateTrackRectangle", NoPauseNoDelay),
  PushWindowTextToggleButton: ac<{windowId: string, key: string, value: boolean}>("PushWindowTextToggleButton", {unDoable: true}),
  SelectWindowDataItemCell: ac<{actionPayload: CustomWindowDataItemPayload}>('SelectWindowDataItemCell', {unDoable: true}),
  UnselectWindowDataItemCell: ac<{actionPayload: CustomWindowDataItemPayload}>('UnselectWindowDataItemCell', {unDoable: true}),
  SystemSetWindowButtonColor: ac<void>("SystemSetWindowButtonColor", NoPauseNoDelay),
  SystemUpdateGeneralButtonProperties: ac<void>("SystemUpdateGeneralButtonProperties", NoPauseNoDelay),
  SystemSetWindowDropdownChoices: ac<void>("SystemSetWindowDropdownChoices", NoPauseNoDelay),
  SystemUpdateWindowDropdownChoices: ac<void>("SystemUpdateWindowDropdownChoices", NoPauseNoDelay),
  SystemSetDataItemsFromResource: ac<void>("SystemSetDataItemsFromResource", NoPauseNoDelay),
  SystemSelectWindowDataItemCell: ac<void>("SystemSelectWindowDataItemCell", NoPauseNoDelay),
  SystemUnselectWindowDataItemCell: ac<void>("SystemUnselectWindowDataItemCell", NoPauseNoDelay),
  TCTMainWindowButtonClick: ac<{id: string}>("TCTMainWindowButtonClick", {unDoable: true}),
  TCTPhoneListWindowButtonClick: ac<{id: string}>("TCTPhoneListWindowButtonClick"),
  TCTPhoneWindowButtonClick: ac<{id: string}>("TCTPhoneWindowButtonClick"),
  TCTPhoneWindowDialPhone: ac<{id: string, value: string}>("TCTPhoneWindowDialPhone"),
  TCTSelectCommunicationUser: ac<{id: string}>("TCTSelectCommunicationUser"),
  TCTSelectPhoneListItem: ac<{id: string, value: string, isSelected: boolean}>("TCTSelectPhoneListItem", {unDoable: true}),
  TCTSettingsConfigWindowActivateCode: ac<{id: string, value: string}>("TCTSettingsConfigWindowActivateCode"),
  TCTSettingsConfigWindowButtonClick: ac<{id: string}>("TCTSettingsConfigWindowButtonClick"),
  TCTSettingsWindowButtonClick: ac<{id: string}>("TCTSettingsWindowButtonClick"),
  SystemTCTInitiatePhonecall: ac<void>("SystemTCTInitiatePhonecall", NoPauseNoDelay),
  SystemTCTPhonecallAccepted: ac<void>("SystemTCTPhonecallAccepted", NoPauseNoDelay),
  SystemTCTAddUsers: ac<void>("SystemTCTAddUsers", NoPauseNoDelay),
  SystemTCTSetPhoneList: ac<void>("SystemTCTSetPhoneList", NoPauseNoDelay),
  SystemSelectWindowDataItems: ac<void>("SystemSelectWindowDataItems", NoPauseNoDelay),
  SystemUnselectWindowDataItems: ac<void>("SystemUnselectWindowDataItems", NoPauseNoDelay),
  SystemSetItemsToRoleStatusWindow: ac<void>("SystemSetItemsToRoleStatusWindow", NoPauseNoDelay),
  SystemIlluminateTouchPanelButton: ac<void>("SystemIlluminateTouchPanelButton", NoPauseNoDelay),
  SystemBlinkingTouchPanelButton: ac<void>("SystemBlinkingTouchPanelButton", NoPauseNoDelay),
  SystemEnableTouchPanelButton: ac<void>("SystemEnableTouchPanelButton", NoPauseNoDelay),
  SystemDisableTouchPanelButton: ac<void>("SystemDisableTouchPanelButton", NoPauseNoDelay),
  SystemUpdateTextPanel: ac<void>("SystemUpdateTextPanel", NoPauseNoDelay),
  SystemReviewAlert: ac<void>("SystemReviewAlert", NoPauseNoDelay),
  ActivateCatizTab: ac<{tabTitle: string}>("ActivateCatizTab", {unDoable: true}),
  ShowTutorialInTutorialArea: ac<void>("ShowTutorialInTutorialArea", NoPauseNoDelay),
  SystemUpdateGlobalTabButtonColor: ac<void>("SystemUpdateGlobalTabButtonColor", NoPauseNoDelay),
  SystemSetCatizLayout: ac<void>("SystemSetCatizLayout", NoPauseNoDelay),
  PushCatizMenuItem: ac<{menuItemId: string}>("PushCatizMenuItem"),
  PushHermesysMenuItem: ac<{menuItemId: string}>("PushHermesysMenuItem"),
  UserClickWindowDataItemCellCheckbox: ac<{actionPayload: any}>('UserClickWindowDataItemCellCheckbox'),
  SystemTCTEnableEntry: ac<void>("SystemTCTEnableEntry", NoPauseNoDelay),
  TCTPhoneListClick: ac<{id: string}>("TCTPhoneListClick"),
  SystemTCTShowStatusMessage: ac<void>("SystemTCTShowStatusMessage", NoPauseNoDelay),
  SystemTCTClearStatusMessage: ac<void>("SystemTCTClearStatusMessage", NoPauseNoDelay),
  SystemUpdateTouchPanelButtonProperties: ac<void>("SystemUpdateTouchPanelButtonProperties", NoPauseNoDelay),
  SystemSetGFCSProcessedVideo: ac<void>("SystemSetGFCSProcessedVideo", NoPauseNoDelay),
  SystemSetGFCSAuxiliarVideo: ac<void>("SystemSetGFCSAuxiliarVideo", NoPauseNoDelay)
};
